import type { ReactNode } from 'react';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import { impactFeedback, selectionFeedback } from '@/lib/native';

/**
 * The app's one overlay, built on the kit's Drawer (vaul) rather than a hand-rolled fixed div.
 *
 * A bottom sheet is the native idiom on both platforms and it lands inside thumb reach; a centred
 * web-style dialog puts its buttons in the hardest place to touch. Using the kit's Drawer means the
 * drag-to-dismiss, focus trap, scroll locking and accessibility come from a maintained component —
 * this file only adds the haptics and the app's own vocabulary on top.
 *
 * Add a new overlay by rendering another BottomSheet. Never hand-roll one, or the app ends up with
 * several dialogs that each behave slightly differently.
 */
export function BottomSheet({
	open,
	onClose,
	title,
	description,
	children,
	footer,
}: {
	open: boolean;
	onClose: () => void;
	title: string;
	description?: string;
	children?: ReactNode;
	footer?: ReactNode;
}) {
	return (
		<Drawer
			open={open}
			onOpenChange={(next) => {
				if (next) {
					void impactFeedback();
					return;
				}
				void selectionFeedback();
				onClose();
			}}
		>
			<DrawerContent className="pb-[env(safe-area-inset-bottom)]">
				<DrawerHeader className="text-left">
					<DrawerTitle style={{ fontFamily: 'var(--brand-display)' }}>{title}</DrawerTitle>
					{description && <DrawerDescription>{description}</DrawerDescription>}
				</DrawerHeader>

				{children && <div className="px-4 pb-2">{children}</div>}

				{footer && <DrawerFooter>{footer}</DrawerFooter>}
				{/* Rendered so Escape and the drag handle always have a close target, even with a custom footer. */}
				<DrawerClose className="sr-only">Close</DrawerClose>
			</DrawerContent>
		</Drawer>
	);
}
